import { type ReactElement } from 'react';
import {
  tableV2ActionsColumnWidth,
  tableV2ChevronColumnWidth,
} from '@/features/shared/components/tableSystem/TableV2Primitives';
import type { MetricThresholdGroupKey } from '@/shared/utils/metricsThresholds';

type TestingEntityMetricColumnWidths = {
  dateTime: number;
  metric: number;
  name: number;
  project: number;
  status: number;
  tags: number;
};

const testingEntityMetricColumnWidthsByCount = {
  0: { name: 38, status: 16, dateTime: 20, metric: 0, tags: 23, project: 0 },
  1: { name: 36, status: 14, dateTime: 18, metric: 12, tags: 17, project: 0 },
  2: { name: 34, status: 13, dateTime: 17, metric: 11, tags: 11, project: 0 },
  3: { name: 30, status: 12, dateTime: 15, metric: 10, tags: 10, project: 0 },
  4: { name: 24, status: 10, dateTime: 13, metric: 10, tags: 10, project: 0 },
} satisfies Record<number, TestingEntityMetricColumnWidths>;

const testingEntityProjectColumnWidthsByCount = {
  0: { name: 30, status: 13, dateTime: 18, metric: 0, tags: 18, project: 18 },
  1: { name: 30, status: 12, dateTime: 16, metric: 11, tags: 15, project: 13 },
  2: { name: 28, status: 11, dateTime: 15, metric: 10, tags: 13, project: 13 },
  3: { name: 25, status: 10, dateTime: 13, metric: 9, tags: 11, project: 11 },
  4: { name: 22, status: 9, dateTime: 12, metric: 9, tags: 9, project: 12 },
} satisfies Record<number, TestingEntityMetricColumnWidths>;

const resolveTestingEntityMetricColumnWidths = (
  visibleMetricCount: number,
  includeProject: boolean,
): TestingEntityMetricColumnWidths => {
  const widthsByCount = includeProject
    ? testingEntityProjectColumnWidthsByCount
    : testingEntityMetricColumnWidthsByCount;

  switch (visibleMetricCount) {
    case 0:
      return widthsByCount[0];
    case 1:
      return widthsByCount[1];
    case 2:
      return widthsByCount[2];
    case 3:
      return widthsByCount[3];
    case 4:
    default:
      return widthsByCount[4];
  }
};

export const buildTestingEntityMetricColgroup = (
  visibleMetricKeys: MetricThresholdGroupKey[],
  includeProject = false,
): ReactElement => {
  const widths = resolveTestingEntityMetricColumnWidths(visibleMetricKeys.length, includeProject);

  return (
    <colgroup>
      <col width={tableV2ChevronColumnWidth} />
      <col width={`${String(widths.name)}%`} />
      <col width={`${String(widths.status)}%`} />
      <col width={`${String(widths.dateTime)}%`} />
      {visibleMetricKeys.map((key) => (
        <col key={key} width={`${String(widths.metric)}%`} />
      ))}
      <col width={`${String(widths.tags)}%`} />
      {includeProject ? <col width={`${String(widths.project)}%`} /> : null}
      <col width={tableV2ActionsColumnWidth} />
    </colgroup>
  );
};
